import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import api from '@/services/api';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  ChartBarIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { TenantSubscriptionPage } from '@/pages/admin/TenantSubscriptionPage';
import { StripeSetupForm } from '@/components/billing/StripeSetupForm';
import { PePortfolioChildTenantPanel } from '@/components/pe-portfolio/PePortfolioChildTenantPanel';

interface TenantPreferences {
  id: string;
  name: string;
  enable_role_sync: boolean;
}

const TAB_IDS = ['roles', 'tenant-info', 'billing-card', 'subscription', 'parent-management'] as const;
type TabId = (typeof TAB_IDS)[number];

function isTabId(s: string | undefined): s is TabId {
  return s != null && (TAB_IDS as readonly string[]).includes(s);
}

const TenantConfigurationPage: React.FC = () => {
  const { tabId } = useParams<{ tabId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stripeReturnToastShown = useRef(false);
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<TenantPreferences | null>(null);
  const [billingReady, setBillingReady] = useState<boolean>(false);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const [cardClientSecret, setCardClientSecret] = useState<string | null>(null);
  const [cardSetupIntentId, setCardSetupIntentId] = useState<string | null>(null);
  const [showCardModal, setShowCardModal] = useState(false);

  const showParentTab = user?.tenantType !== 'PORTFOLIO';

  const activeTab: TabId = useMemo(() => {
    if (!isTabId(tabId)) return 'roles';
    if (tabId === 'parent-management' && !showParentTab) return 'roles';
    return tabId;
  }, [tabId, showParentTab]);

  useEffect(() => {
    if (!tabId) return;
    if (!isTabId(tabId)) {
      navigate('/tenant-configuration/roles', { replace: true });
      return;
    }
    if (tabId === 'parent-management' && !showParentTab) {
      navigate('/tenant-configuration/roles', { replace: true });
    }
  }, [tabId, showParentTab, navigate]);

  useEffect(() => {
    if (stripeReturnToastShown.current) return;
    if (searchParams.get('card') !== 'complete' || activeTab !== 'billing-card') return;
    stripeReturnToastShown.current = true;
    toast.success(t('businessPreferences.billingCard.saved') || 'Billing card saved successfully.');
    const next = new URLSearchParams(searchParams);
    next.delete('card');
    setSearchParams(next, { replace: true });
  }, [activeTab, searchParams, setSearchParams, t]);

  useEffect(() => {
    if (user?.tenantId) {
      loadTenantPreferences();
      loadStripeConfig();
      loadTenantBillingStatus();
    }
  }, [user?.tenantId]);

  const loadTenantPreferences = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/tenants/${user?.tenantId}`);

      if (response.data.success) {
        setPreferences({
          id: response.data.data.id,
          name: response.data.data.name,
          enable_role_sync: response.data.data.enable_role_sync ?? false,
        });
      }
    } catch (error: unknown) {
      console.error('Error loading tenant preferences:', error);
      toast.error(t('businessPreferences.failedToLoad') || 'Failed to load business preferences');
    } finally {
      setLoading(false);
    }
  };

  const loadStripeConfig = async () => {
    try {
      const res = await api.get<{ publishableKey: string | null }>('/ai/billing/tenant/stripe-config');
      const pk =
        res.data?.publishableKey ??
        (import.meta as unknown as { env?: { VITE_STRIPE_PUBLISHABLE_KEY?: string } }).env?.VITE_STRIPE_PUBLISHABLE_KEY ??
        null;
      setStripePublishableKey(pk || null);
    } catch (error) {
      console.error('Error loading Stripe config for tenant configuration:', error);
      setStripePublishableKey(null);
    }
  };

  const loadTenantBillingStatus = async () => {
    try {
      const res = await api.get<{ billingReady: boolean }>('/ai/billing/tenant/status');
      setBillingReady(res.data?.billingReady === true);
    } catch (error) {
      console.error('Error loading tenant billing status:', error);
      setBillingReady(false);
    }
  };

  const handleUpdatePreference = async (key: keyof TenantPreferences, value: boolean | string) => {
    if (!preferences || !user?.tenantId) return;

    try {
      setSaving(true);

      const response = await api.put(`/tenants/${user.tenantId}`, {
        [key]: value,
      });

      if (response.data.success) {
        setPreferences({
          ...preferences,
          [key]: value,
        });
        toast.success(t('businessPreferences.preferenceUpdated') || 'Preference updated successfully');
      }
    } catch (error: unknown) {
      console.error(`Error updating preference ${String(key)}:`, error);
      toast.error(t('businessPreferences.failedToUpdate') || 'Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRoleSync = async () => {
    if (!preferences) return;
    const newValue = !preferences.enable_role_sync;
    await handleUpdatePreference('enable_role_sync', newValue);

    if (newValue) {
      toast.success(t('businessPreferences.roleSyncEnabled') || 'Role sync enabled successfully');
    } else {
      toast.success(t('businessPreferences.roleSyncDisabled') || 'Role sync disabled successfully');
    }
  };

  const setTab = (id: TabId) => {
    navigate(`/tenant-configuration/${id}`);
  };

  const allTabs: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }>; show: boolean }[] = [
    {
      id: 'roles',
      label: t('businessPreferences.rolePermissions.title') || 'Roles & permissions',
      icon: Cog6ToothIcon,
      show: true,
    },
    {
      id: 'tenant-info',
      label: t('businessPreferences.tenantInfo') || 'Tenant information',
      icon: BuildingOfficeIcon,
      show: true,
    },
    {
      id: 'billing-card',
      label: t('businessPreferences.billingCard.title') || 'Tenant billing card',
      icon: CreditCardIcon,
      show: true,
    },
    {
      id: 'subscription',
      label: 'Subscription & billing',
      icon: ChartBarIcon,
      show: true,
    },
    {
      id: 'parent-management',
      label: 'Parent management',
      icon: LinkIcon,
      show: showParentTab,
    },
  ];

  const visibleTabs = allTabs.filter((x) => x.show);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="border border-[var(--color-border)] rounded-lg p-4">
            <p className="text-sm text-[var(--color-secondary-text)]">
              {t('businessPreferences.loading') || 'Loading preferences...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="border border-[var(--color-border)] rounded-lg p-4">
            <p className="text-sm text-[var(--color-secondary-text)]">
              {t('businessPreferences.notFound') || 'Business preferences not found'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="border border-[var(--color-border)] rounded-lg p-4">
          <div className="mb-2">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
                  {t('businessPreferences.title') || 'Business Preferences'}
                </h1>
                <p className="mt-0.5 text-xs text-[var(--color-secondary-text)]">
                  {t('businessPreferences.description') || 'Manage your tenant business settings and preferences'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="tab-bar">
              <nav className="flex gap-1 overflow-x-auto items-end" aria-label="Tenant configuration tabs">
                {visibleTabs.map(({ id, label, icon: Icon }) => {
                  const isActive = activeTab === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setTab(id)}
                      className={`tab-button inline-flex items-center gap-2 ${isActive ? 'tab-button-selected' : ''}`}
                    >
                      <Icon className="h-5 w-5 shrink-0 [stroke:currentColor]" />
                      {label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          <div className="mt-6">
            {activeTab === 'roles' && (
              <div className="border border-[var(--color-border)] rounded-lg p-4 md:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Cog6ToothIcon className="w-5 h-5 text-[var(--color-info)]" />
                  <h2 className="text-lg font-semibold text-[var(--color-secondary-text)]">
                    {t('businessPreferences.rolePermissions.title') || 'Role & Permission Preferences'}
                  </h2>
                </div>

                <div className="border-b border-[var(--color-border)] pb-6 mb-6 last:border-b-0 last:pb-0 last:mb-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowPathIcon className="w-5 h-5 text-[var(--color-info)]" />
                        <h3 className="text-base font-semibold text-[var(--color-secondary-text)]">
                          {t('businessPreferences.roleSync.title') || 'Platform Role Synchronization'}
                        </h3>
                      </div>
                      <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mb-4">
                        {t('businessPreferences.roleSync.description') ||
                          'When enabled, new permissions added to platform roles will automatically be synced to your tenant role copies. You can still manage your tenant roles independently.'}
                      </p>

                      <div className="bg-[var(--color-info)]/10 border border-[var(--color-info)]/30 rounded-md p-4 mb-4">
                        <div className="flex items-start gap-2">
                          <InformationCircleIcon className="w-5 h-5 text-[var(--color-info)] flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-[var(--color-secondary-text)]">
                            <p className="font-medium mb-1">
                              {t('businessPreferences.roleSync.infoTitle') || 'How it works:'}
                            </p>
                            <ul className="list-disc list-inside space-y-1 opacity-90">
                              <li>
                                {t('businessPreferences.roleSync.info1') ||
                                  'New permissions added to platform roles are automatically added to your tenant copies'}
                              </li>
                              <li>
                                {t('businessPreferences.roleSync.info2') ||
                                  'Permission removals are NOT synced - you manage those independently'}
                              </li>
                              <li>
                                {t('businessPreferences.roleSync.info3') ||
                                  'You can customize role names, descriptions, and permissions at any time'}
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-[var(--color-secondary-text)]">
                            {t('businessPreferences.roleSync.enableSync') || 'Enable automatic role sync'}
                          </label>
                          <p className="text-xs text-[var(--color-secondary-text)] opacity-60 mt-1">
                            {preferences.enable_role_sync
                              ? t('businessPreferences.roleSync.currentlyEnabled') || 'Currently enabled'
                              : t('businessPreferences.roleSync.currentlyDisabled') || 'Currently disabled'}
                          </p>
                        </div>
                        <button
                          onClick={() => void handleToggleRoleSync()}
                          disabled={saving}
                          className={`
                      relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                      ${preferences.enable_role_sync ? 'bg-[var(--color-success)]' : 'bg-[var(--color-border)]'}
                      ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                          type="button"
                        >
                          <span
                            className={`
                        inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                        ${preferences.enable_role_sync ? 'translate-x-6' : 'translate-x-1'}
                      `}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'tenant-info' && (
              <div className="border border-[var(--color-border)] rounded-lg p-4 md:p-6">
                <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">
                  {t('businessPreferences.tenantInfo') || 'Tenant Information'}
                </h2>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-[var(--color-secondary-text)]">
                      {t('businessPreferences.tenantName') || 'Tenant Name:'}
                    </span>
                    <span className="ml-2 text-[var(--color-secondary-text)] opacity-75">{preferences.name}</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'billing-card' && (
              <div className="border border-[var(--color-border)] rounded-lg p-4 md:p-6">
                <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-2">
                  {t('businessPreferences.billingCard.title') || 'Tenant Billing Card'}
                </h2>
                <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mb-4">
                  {t('businessPreferences.billingCard.description') ||
                    'Provide a billing card for this tenant. This card will be used for all centrally billed AI plans assigned to your users.'}
                </p>
                {stripePublishableKey ? (
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-sm text-[var(--color-secondary-text)] opacity-80">
                      {billingReady
                        ? t('businessPreferences.billingCard.statusReady') ||
                          'A billing card is currently on file for this tenant.'
                        : t('businessPreferences.billingCard.statusNotReady') ||
                          'No billing card is on file yet for this tenant.'}
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const res = await api.post<{
                            clientSecret: string;
                            setupIntentId: string;
                          }>('/ai/billing/tenant/payment-method/setup-intent');
                          setCardClientSecret(res.data.clientSecret);
                          setCardSetupIntentId(res.data.setupIntentId);
                          setShowCardModal(true);
                        } catch (error: unknown) {
                          console.error('Error creating setup intent for billing card:', error);
                          const err = error as { response?: { data?: { message?: string; error?: string } } };
                          const msg =
                            err?.response?.data?.message ||
                            err?.response?.data?.error ||
                            'Failed to start card setup.';
                          toast.error(msg);
                        }
                      }}
                      className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md hover:bg-[var(--color-primary-bg)]/90 text-sm shrink-0"
                    >
                      {t('businessPreferences.billingCard.manageButton') || 'Add / Update card'}
                    </button>
                  </div>
                ) : (
                  <div className="text-sm text-red-600">
                    {t('businessPreferences.billingCard.noStripe') ||
                      'Stripe billing provider is not configured. Contact platform administrator.'}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'subscription' && (
              <div className="border border-[var(--color-border)] rounded-lg p-4 md:p-6">
                <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-2">Subscription & Billing</h2>
                <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mb-4">
                  Manage your tenant&apos;s subscription and billing card. This card will be used for all centrally billed
                  AI plans assigned to your users.
                </p>
                <div className="border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] p-4">
                  <TenantSubscriptionPage embedded hideTopUpSection />
                </div>
              </div>
            )}

            {activeTab === 'parent-management' && showParentTab && (
              <div className="border border-[var(--color-border)] rounded-lg p-4 md:p-6">
                <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-1">Parent management</h2>
                <p className="text-sm text-[var(--color-secondary-text)] mb-4">
                  Connect this organization to a private equity portfolio for governance and reporting. Only tenant
                  administrators can submit or withdraw link requests.
                </p>
                <PePortfolioChildTenantPanel />
              </div>
            )}
          </div>
        </div>

        {showCardModal && cardClientSecret && stripePublishableKey && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
                {t('businessPreferences.billingCard.modalTitle') || 'Set up billing card'}
              </h3>
              <StripeSetupForm
                clientSecret={cardClientSecret}
                publishableKey={stripePublishableKey}
                submitLabel={t('businessPreferences.billingCard.saveButton') || 'Save card'}
                billingDetails={{
                  name: [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || undefined,
                  email: user?.email || undefined,
                }}
                onSuccess={async () => {
                  try {
                    if (cardSetupIntentId) {
                      await api.post('/ai/billing/tenant/payment-method/confirm', {
                        setupIntentId: cardSetupIntentId,
                      });
                    }
                    setBillingReady(true);
                    toast.success(t('businessPreferences.billingCard.saved') || 'Billing card saved successfully.');
                  } catch (error: unknown) {
                    console.error('Error confirming billing card:', error);
                    const err = error as { response?: { data?: { message?: string; error?: string } } };
                    const msg =
                      err?.response?.data?.message ||
                      err?.response?.data?.error ||
                      'Failed to confirm billing card.';
                    toast.error(msg);
                    return;
                  } finally {
                    setShowCardModal(false);
                    setCardClientSecret(null);
                    setCardSetupIntentId(null);
                  }
                }}
                onCancel={() => {
                  setShowCardModal(false);
                  setCardClientSecret(null);
                  setCardSetupIntentId(null);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantConfigurationPage;
